import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import moment from 'moment-timezone';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import DOMPurify from 'dompurify';
import {
  FaUpload,
  FaEye,
  FaRegFileAlt,
  FaRegFileImage,
  FaRegFilePdf,
  FaRegFileWord,
  FaRegFile
} from 'react-icons/fa';
import { post_api, api_with_method } from '../../../../redux/api_funcs';
import { get_api_url } from '../../../../utils/urls';
import { getCookie } from '../../../../util_functions';

const HTML_TAG_RE = /<\/?[a-z][\s\S]*?>/i;
const is_html_content = (value) => typeof value === 'string' && HTML_TAG_RE.test(value);

// Strip tags via regex (no DOM parse) to check whether the Quill payload has
// any visible text. Safe because we never render this output as HTML.
const quill_to_text = (html) => {
  if (!html) return '';
  return html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .trim();
};

const QUILL_MODULES = {
  toolbar: [
    ['bold', 'italic'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link', 'clean']
  ]
};
const QUILL_FORMATS = ['bold', 'italic', 'list', 'bullet', 'link'];

const escape_html = (s) =>
  String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

// WhatsApp-export line header. Matches both iOS/Android formats:
//   [11:44 am, 21/05/2026] Swetha Hr Myt: body
//   [21/05/2026, 11:44 am] +91 90257 57170: body
const WA_HEADER_RE =
  /^\[\s*(?:(\d{1,2}:\d{2}(?::\d{2})?\s*(?:am|pm)?)\s*,\s*(\d{1,2}\/\d{1,2}\/\d{2,4})|(\d{1,2}\/\d{1,2}\/\d{2,4})\s*,\s*(\d{1,2}:\d{2}(?::\d{2})?\s*(?:am|pm)?))\s*\]\s+([^:\n]{1,80}?):\s*(.*)$/i;

const looks_like_whatsapp = (text) => {
  if (!text) return false;
  const lines = text.split(/\r?\n/);
  let hits = 0;
  for (let i = 0; i < lines.length && hits < 2; i += 1) {
    if (WA_HEADER_RE.test(lines[i])) hits += 1;
  }
  return hits >= 1;
};

const collapse_blank_lines = (text) => text.replace(/\n{3,}/g, '\n\n');

const text_to_paragraphs_html = (text) =>
  collapse_blank_lines(text.replace(/\r\n/g, '\n'))
    .split(/\n{2,}/)
    .map((para) => {
      const t = para.trim();
      if (!t) return '';
      const escaped = escape_html(t).replace(/\n/g, '<br>');
      return `<p>${escaped}</p>`;
    })
    .join('');

const whatsapp_to_html = (text) => {
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  const messages = [];
  let current = null;
  const flush_preamble = [];

  for (const line of lines) {
    const m = line.match(WA_HEADER_RE);
    if (m) {
      if (current) messages.push(current);
      const time = (m[1] || m[4] || '').trim();
      const date = (m[2] || m[3] || '').trim();
      const sender = m[5].trim();
      const first_body = m[6] || '';
      current = { time, date, sender, body: first_body ? [first_body] : [] };
    } else if (current) {
      current.body.push(line);
    } else if (line.trim()) {
      flush_preamble.push(line);
    }
  }
  if (current) messages.push(current);

  let html = '';
  if (flush_preamble.length) {
    html += text_to_paragraphs_html(flush_preamble.join('\n'));
  }
  html += messages
    .map((msg) => {
      const header = `<p><strong>${escape_html(msg.sender)}</strong>${
        msg.time || msg.date
          ? ` <em>${escape_html([msg.time, msg.date].filter(Boolean).join(' · '))}</em>`
          : ''
      }</p>`;
      const body_raw = msg.body.join('\n').replace(/^\s+|\s+$/g, '');
      const body_html = body_raw ? text_to_paragraphs_html(body_raw) : '<p><br></p>';
      return header + body_html;
    })
    .join('');
  return html;
};

const AVATAR_PALETTE = [
  { bg: '#F0997B', fg: '#4A1B0C' },
  { bg: '#AFA9EC', fg: '#26215C' },
  { bg: '#7BC5A6', fg: '#0F3D2A' },
  { bg: '#F2C879', fg: '#5A3C0A' },
  { bg: '#9ECBE6', fg: '#143850' },
  { bg: '#E69EC8', fg: '#52123E' }
];

const IMAGE_EXTS = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg']);
const PDF_EXTS = new Set(['pdf']);

const ext_of = (filename) => (filename || '').split('.').pop().toLowerCase();

const initials_of = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const palette_for = (name) => {
  if (!name) return AVATAR_PALETTE[0];
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) hash = (hash * 31 + name.charCodeAt(i)) | 0;
  return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length];
};

const FILE_ICON = {
  pdf: { Icon: FaRegFilePdf, color: '#A32D2D' },
  doc: { Icon: FaRegFileWord, color: '#185FA5' },
  docx: { Icon: FaRegFileWord, color: '#185FA5' },
  jpg: { Icon: FaRegFileImage, color: '#0F6E56' },
  jpeg: { Icon: FaRegFileImage, color: '#0F6E56' },
  png: { Icon: FaRegFileImage, color: '#0F6E56' },
  gif: { Icon: FaRegFileImage, color: '#0F6E56' },
  txt: { Icon: FaRegFileAlt, color: '#6B7280' }
};

const file_icon_for = (filename) =>
  FILE_ICON[ext_of(filename)] || { Icon: FaRegFile, color: '#6B7280' };

const day_label = (date, tz) => {
  const m = tz ? moment(date).tz(tz) : moment(date);
  const today = tz ? moment().tz(tz) : moment();
  const yesterday = today.clone().subtract(1, 'day');
  if (m.isSame(today, 'day')) return `TODAY · ${m.format('MMM D').toUpperCase()}`;
  if (m.isSame(yesterday, 'day')) return `YESTERDAY · ${m.format('MMM D').toUpperCase()}`;
  if (m.isSame(today, 'year')) return m.format('MMM D').toUpperCase();
  return m.format('MMM D, YYYY').toUpperCase();
};

const time_label = (date, tz) => (tz ? moment(date).tz(tz) : moment(date)).format('h:mm A');

const group_by_day = (items, tz) => {
  const groups = [];
  let current = null;
  items.forEach((item) => {
    const key = (tz ? moment(item.created_date).tz(tz) : moment(item.created_date)).format(
      'YYYY-MM-DD'
    );
    if (!current || current.key !== key) {
      current = { key, label: day_label(item.created_date, tz), items: [] };
      groups.push(current);
    }
    current.items.push(item);
  });
  return groups;
};

function Avatar({ name }) {
  const palette = palette_for(name);
  return (
    <div
      style={{
        width: 28,
        height: 28,
        borderRadius: '50%',
        background: palette.bg,
        color: palette.fg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 500,
        fontSize: 11,
        flexShrink: 0
      }}
    >
      {initials_of(name)}
    </div>
  );
}

function NoteRow({ note, tz }) {
  const author = note.created_by_name || 'Unknown';
  const [expanded, setExpanded] = useState(false);
  const raw = note.value || '';
  const is_html = is_html_content(raw);
  const plain_for_clip = is_html ? quill_to_text(raw) : raw;
  const should_clip = !is_html && plain_for_clip.length > 240;
  const shown_text = expanded || !should_clip ? raw : `${raw.slice(0, 240)}…`;

  const bubble_base_style = {
    background: 'var(--surface-sunken)',
    color: 'var(--text-primary)',
    padding: '8px 12px',
    borderRadius: 8,
    fontSize: 13.5,
    lineHeight: 1.5,
    wordBreak: 'break-word'
  };

  // Sanitize Quill HTML before rendering. allow target=_blank on links.
  const sanitized = useMemo(
    () => (is_html ? DOMPurify.sanitize(raw, { ADD_ATTR: ['target', 'rel'] }) : ''),
    [raw, is_html]
  );

  return (
    <div style={{ display: 'flex', gap: 10 }}>
      <Avatar name={author} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            marginBottom: 4
          }}
        >
          <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>
            {author}{' '}
            <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400 }}>
              · {time_label(note.created_date, tz)}
            </span>
          </span>
        </div>
        {is_html ? (
          <div
            className="sn-bubble"
            style={bubble_base_style}
            // sanitized by DOMPurify above
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: sanitized }}
          />
        ) : (
          <div className="sn-bubble" style={{ ...bubble_base_style, whiteSpace: 'pre-wrap' }}>
            {shown_text}
            {should_clip ? (
              <>
                <br />
                <span
                  role="button"
                  tabIndex={0}
                  onClick={() => setExpanded((v) => !v)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') setExpanded((v) => !v);
                  }}
                  style={{ color: 'var(--state-info)', fontSize: 12, cursor: 'pointer' }}
                >
                  {expanded ? 'Show less' : 'Show more'}
                </span>
              </>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

function DocumentRow({ doc, tz, on_preview }) {
  const author = doc.created_by_name || 'Unknown';
  const { Icon, color } = file_icon_for(doc.filename);
  // Images with a signed URL preview inline; everything else stays a file row.
  const is_image = IMAGE_EXTS.has(ext_of(doc.filename)) && !!doc.signed_url;
  return (
    <div style={{ display: 'flex', gap: 10 }}>
      <Avatar name={author} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            marginBottom: 4
          }}
        >
          <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>
            {author}{' '}
            <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400 }}>
              · {time_label(doc.created_date, tz)}
            </span>
          </span>
        </div>
        {is_image ? (
          <div>
            <img
              src={doc.signed_url}
              alt={doc.filename}
              onClick={() => on_preview(doc)}
              style={{
                maxWidth: '100%',
                maxHeight: 260,
                borderRadius: 8,
                border: '0.5px solid var(--border-default)',
                cursor: 'zoom-in',
                display: 'block'
              }}
            />
            <p style={{ fontSize: 11.5, color: 'var(--text-muted)', margin: '4px 0 0' }}>
              {doc.filename}
            </p>
          </div>
        ) : (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: 10,
              border: '0.5px solid var(--border-default)',
              background: 'var(--surface-sunken)',
              borderRadius: 8
            }}
          >
            <Icon style={{ fontSize: 24, color, flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <p
                style={{
                  fontSize: 13.5,
                  fontWeight: 500,
                  margin: 0,
                  color: 'var(--text-primary)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {doc.filename}
              </p>
              <p style={{ fontSize: 11.5, color: 'var(--text-muted)', margin: '2px 0 0' }}>
                {ext_of(doc.filename).toUpperCase() || 'FILE'}
              </p>
            </div>
            <button
              type="button"
              onClick={() => on_preview(doc)}
              aria-label="Preview"
              title="Preview"
              style={{
                padding: '6px 10px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                background: 'var(--surface-card)',
                border: '0.5px solid var(--border-default)',
                borderRadius: 6,
                color: 'var(--text-secondary)',
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: 500,
                flexShrink: 0
              }}
            >
              <FaEye style={{ fontSize: 13 }} />
              Preview
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function PreviewOverlay({ doc, on_close, set_notification_variable }) {
  const [object_url, set_object_url] = useState('');
  const [loading, set_loading] = useState(true);
  const [content_type, set_content_type] = useState('');
  const created_url_ref = useRef('');
  const on_close_ref = useRef(on_close);
  const notify_ref = useRef(set_notification_variable);

  useEffect(() => {
    on_close_ref.current = on_close;
    notify_ref.current = set_notification_variable;
  }, [on_close, set_notification_variable]);

  const doc_uuid = doc?.uuid;
  const signed_url = doc?.signed_url;

  useEffect(() => {
    if (!doc_uuid) return undefined;
    let cancelled = false;
    set_loading(true);
    set_object_url('');
    set_content_type('');
    // Prefer the signed S3 URL (v2 attachments) — fetched directly, no auth
    // header. Fall back to the legacy attachment proxy for v1 documents.
    const url = signed_url || get_api_url(`get_student_attachment?attachment_uuid=${doc_uuid}`);
    axios({
      method: 'get',
      url,
      headers: signed_url ? {} : { Authorization: `Bearer ${getCookie('cms_token_3')}` },
      responseType: 'blob'
    })
      .then((res) => {
        if (cancelled) return;
        const type = res.headers['content-type'] || res.data.type || 'application/octet-stream';
        const blob = new Blob([res.data], { type });
        const obj_url = URL.createObjectURL(blob);
        created_url_ref.current = obj_url;
        set_content_type(type);
        set_object_url(obj_url);
      })
      .catch((e) => {
        if (cancelled) return;
        const message =
          e?.response?.data?.message || e?.response?.data?.reason || 'Failed to load preview';
        notify_ref.current(true, 'error', message);
        on_close_ref.current();
      })
      .finally(() => {
        if (!cancelled) set_loading(false);
      });
    return () => {
      cancelled = true;
      if (created_url_ref.current) {
        URL.revokeObjectURL(created_url_ref.current);
        created_url_ref.current = '';
      }
    };
  }, [doc_uuid, signed_url]);

  if (!doc) return null;

  const ext = ext_of(doc.filename);
  const is_image = IMAGE_EXTS.has(ext) || content_type.startsWith('image/');
  const is_pdf = PDF_EXTS.has(ext) || content_type === 'application/pdf';

  return (
    <div
      role="presentation"
      onClick={on_close}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--overlay-modal-strong)',
        zIndex: 1000000000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20
      }}
    >
      <div
        role="presentation"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface-card)',
          color: 'var(--text-primary)',
          borderRadius: 10,
          width: 'min(960px, 100%)',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: 'var(--shadow-modal)'
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 16px',
            borderBottom: '0.5px solid var(--border-default)',
            gap: 12
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontSize: 14,
                fontWeight: 500,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {doc.filename}
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 2 }}>
              Uploaded by {doc.created_by_name || 'Unknown'}
            </div>
          </div>
          <button
            type="button"
            onClick={on_close}
            aria-label="Close preview"
            style={{
              padding: '6px 14px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              background: 'var(--surface-card)',
              border: '0.5px solid var(--border-default)',
              borderRadius: 6,
              color: 'var(--text-primary)',
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 500,
              lineHeight: 1,
              flexShrink: 0
            }}
          >
            <span aria-hidden="true" style={{ fontSize: 16, lineHeight: 1 }}>
              ×
            </span>
            Close
          </button>
        </div>
        <div
          style={{
            flex: 1,
            minHeight: 320,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'var(--surface-sunken)',
            padding: is_image ? 16 : 0,
            overflow: 'auto'
          }}
        >
          {loading ? (
            <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Loading preview…</div>
          ) : null}
          {!loading && object_url && is_image ? (
            <img
              src={object_url}
              alt={doc.filename}
              style={{ maxWidth: '100%', maxHeight: '76vh', objectFit: 'contain' }}
            />
          ) : null}
          {!loading && object_url && is_pdf ? (
            <iframe
              src={object_url}
              title={doc.filename}
              style={{ width: '100%', height: '76vh', border: 'none', background: '#fff' }}
            />
          ) : null}
          {!loading && object_url && !is_image && !is_pdf ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 10,
                color: 'var(--text-muted)',
                fontSize: 13,
                padding: 32
              }}
            >
              <div>Preview isn’t available for this file type.</div>
              <a
                href={object_url}
                download={doc.filename}
                style={{
                  fontSize: 13,
                  fontWeight: 500,
                  background: 'var(--brand-primary)',
                  color: '#fff',
                  padding: '8px 14px',
                  borderRadius: 6,
                  textDecoration: 'none'
                }}
              >
                Download {doc.filename}
              </a>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function StudentNotes({ student_uuid, iana_timezone, set_notification_variable }) {
  const [notes, set_notes] = useState([]);
  const [documents, set_documents] = useState([]);
  const [loading, set_loading] = useState(true);
  const [composer, set_composer] = useState('');
  const [saving, set_saving] = useState(false);
  const [uploading, set_uploading] = useState(false);
  const [preview_doc, set_preview_doc] = useState(null);
  const file_input_ref = useRef(null);
  const list_ref = useRef(null);
  const quill_ref = useRef(null);

  // Intercept paste — when WhatsApp-formatted text is pasted, reflow it into
  // bold sender + body paragraphs so the saved note is clean instead of a wall
  // of [time, date] noise. Other pastes fall through to Quill's default handler.
  useEffect(() => {
    const quill = quill_ref.current && quill_ref.current.getEditor();
    if (!quill) return undefined;
    const { root } = quill;
    const on_paste = (e) => {
      const cd = e.clipboardData;
      if (!cd) return;
      const text = cd.getData('text/plain');
      if (!text || !looks_like_whatsapp(text)) return;
      e.preventDefault();
      e.stopPropagation();
      const html = whatsapp_to_html(text);
      if (!html) return;
      const range = quill.getSelection(true) || { index: quill.getLength(), length: 0 };
      if (range.length > 0) quill.deleteText(range.index, range.length, 'user');
      quill.clipboard.dangerouslyPasteHTML(range.index, html, 'user');
    };
    root.addEventListener('paste', on_paste, true);
    return () => root.removeEventListener('paste', on_paste, true);
  }, []);

  const load_data = () => {
    set_loading(true);
    const url = get_api_url('/v2/early_retention_checklist/student_notes');
    post_api(url, { student_uuid }, true)
      .then((res) => {
        const data = res?.data?.data || {};
        set_notes(Array.isArray(data.notes) ? data.notes : []);
        set_documents(Array.isArray(data.documents) ? data.documents : []);
      })
      .catch((e) => {
        const message =
          e?.response?.data?.message || e?.response?.data?.reason || 'Failed to load notes';
        set_notification_variable(true, 'error', message);
      })
      .finally(() => set_loading(false));
  };

  useEffect(() => {
    if (student_uuid) load_data();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [student_uuid]);

  const feed_groups = useMemo(() => {
    const items = [
      ...notes.map((n) => ({ ...n, _kind: 'note' })),
      ...documents.map((d) => ({ ...d, _kind: 'document' }))
    ].sort((a, b) => new Date(a.created_date).getTime() - new Date(b.created_date).getTime());
    return group_by_day(items, iana_timezone);
  }, [notes, documents, iana_timezone]);

  // Auto-scroll to the newest item when the feed changes.
  useEffect(() => {
    if (list_ref.current) list_ref.current.scrollTop = list_ref.current.scrollHeight;
  }, [feed_groups]);

  const composer_is_empty = quill_to_text(composer).length === 0;

  const save_note = () => {
    if (composer_is_empty || saving) return;
    const sanitized_payload = DOMPurify.sanitize(composer, { ADD_ATTR: ['target', 'rel'] });
    set_saving(true);
    const url = get_api_url('save_notes');
    post_api(url, { student_uuid, notes: sanitized_payload }, true)
      .then(() => {
        set_composer('');
        load_data();
      })
      .catch((e) => {
        const message =
          e?.response?.data?.message || e?.response?.data?.reason || 'Failed to save note';
        set_notification_variable(true, 'error', message);
      })
      .finally(() => set_saving(false));
  };

  const upload_file = (file) => {
    set_uploading(true);
    const url = get_api_url('/v2/early_retention_checklist/upload_attachment');
    const form = new FormData();
    form.append('student_uuid', student_uuid);
    form.append('folder', 'student_notes');
    form.append('file', file);
    api_with_method('post', url, form, true, 'formdata')
      .then(() => {
        set_notification_variable(true, 'success', 'Document uploaded');
        load_data();
      })
      .catch((e) => {
        const message =
          e?.response?.data?.message || e?.response?.data?.reason || 'Failed to upload document';
        set_notification_variable(true, 'error', message);
      })
      .finally(() => set_uploading(false));
  };

  const trigger_upload = () => {
    if (file_input_ref.current) file_input_ref.current.click();
  };

  const handle_file_change = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    upload_file(file);
    e.target.value = '';
  };

  return (
    <div
      style={{
        background: 'var(--surface-card)',
        color: 'var(--text-primary)',
        borderRadius: 8,
        overflow: 'hidden',
        margin: '-20px'
      }}
    >
      {/* Action bar */}
      <div
        style={{
          padding: '12px 18px',
          borderBottom: '0.5px solid var(--border-default)',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 8
        }}
      >
        <button
          type="button"
          onClick={trigger_upload}
          disabled={uploading}
          style={{
            padding: '8px 14px',
            fontSize: 13,
            fontWeight: 500,
            background: 'var(--brand-primary)',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            cursor: uploading ? 'not-allowed' : 'pointer',
            opacity: uploading ? 0.7 : 1
          }}
        >
          <FaUpload style={{ fontSize: 13 }} />
          {uploading ? 'Uploading…' : 'Upload document'}
        </button>
        <input
          ref={file_input_ref}
          type="file"
          style={{ display: 'none' }}
          onChange={handle_file_change}
        />
      </div>

      {/* Feed */}
      <div
        ref={list_ref}
        style={{
          padding: '16px 18px',
          height: 380,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 14
        }}
      >
        {loading ? (
          <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
            Loading…
          </div>
        ) : null}

        {!loading && feed_groups.length === 0 ? (
          <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
            No notes or documents yet.
          </div>
        ) : null}

        {!loading && feed_groups.length > 0
          ? feed_groups.map((group) => (
              <React.Fragment key={group.key}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ flex: 1, height: '0.5px', background: 'var(--border-default)' }} />
                  <span style={{ fontSize: 11, color: 'var(--text-muted)', letterSpacing: 0.3 }}>
                    {group.label}
                  </span>
                  <div style={{ flex: 1, height: '0.5px', background: 'var(--border-default)' }} />
                </div>
                {group.items.map((item) =>
                  item._kind === 'document' ? (
                    <DocumentRow
                      key={`d-${item.uuid}`}
                      doc={item}
                      tz={iana_timezone}
                      on_preview={set_preview_doc}
                    />
                  ) : (
                    <NoteRow key={`n-${item.uuid}`} note={item} tz={iana_timezone} />
                  )
                )}
              </React.Fragment>
            ))
          : null}
      </div>

      {/* Composer */}
      <div
        className="sn-composer"
        style={{
          padding: '12px 14px',
          borderTop: '0.5px solid var(--border-default)',
          background: 'var(--surface-card)',
          display: 'flex',
          gap: 8,
          alignItems: 'stretch'
        }}
      >
        <div
          style={{
            flex: 1,
            border: '0.5px solid var(--input-border)',
            borderRadius: 6,
            background: 'var(--input-bg)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <ReactQuill
            ref={quill_ref}
            theme="snow"
            value={composer}
            onChange={set_composer}
            modules={QUILL_MODULES}
            formats={QUILL_FORMATS}
            placeholder="Add a note about this student…"
          />
        </div>
        <button
          type="button"
          onClick={save_note}
          disabled={saving || composer_is_empty}
          style={{
            padding: '0 18px',
            alignSelf: 'stretch',
            fontSize: 13,
            fontWeight: 500,
            background: 'var(--brand-primary)',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            cursor: saving || composer_is_empty ? 'not-allowed' : 'pointer',
            opacity: saving || composer_is_empty ? 0.6 : 1,
            minWidth: 80
          }}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>

      {preview_doc ? (
        <PreviewOverlay
          doc={preview_doc}
          on_close={() => set_preview_doc(null)}
          set_notification_variable={set_notification_variable}
        />
      ) : null}
    </div>
  );
}

export default StudentNotes;
